import {
  LeadResearchCostRun,
  useApolloQuota,
  useLandlordResearchTrace,
  useLeadResearchCosts,
  usePeopleDiscoveryTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useState } from "react";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

const usd = (n: number): string => `$${n.toFixed(2)}`;

// Apollo's free daily discovery quota resets at 00:00 UTC. Show how long until then
// so an operator who's hit the cap knows when it tops back up.
const apolloResetHint = (): string => {
  const now = new Date();
  const nextMidnightUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1
  );
  const msLeft = nextMidnightUtc - now.getTime();
  const hours = Math.floor(msLeft / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  const rel = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return `resets in ${rel} (00:00 UTC)`;
};

// The AI-invocation ledger `source` value for contacts-ranking spend (mirror of
// the BE `PEOPLE_DISCOVERY_COST_SOURCE` in operation_people_discovery.py). A run
// tagged with this routes its trace link to the contacts-ranking trace endpoint;
// everything else (landlord research) uses the landlord-research trace endpoint.
const CONTACTS_RANKING_SOURCE = "Contacts ranking";

const LeadResearchCostsCard = () => {
  const [days, setDays] = useState<number>(30);
  const costs = useLeadResearchCosts(days);
  const quota = useApolloQuota();
  const [traceOf, setTraceOf] = useState<LeadResearchCostRun | null>(null);

  // The trace lives on a different endpoint per surface, so call both hooks
  // (hooks can't be conditional) and enable only the one matching the run's
  // source; render whichever is enabled.
  const isContactsTrace = traceOf?.source === CONTACTS_RANKING_SOURCE;
  const landlordTrace = useLandlordResearchTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && !isContactsTrace
  );
  const peopleTrace = usePeopleDiscoveryTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isContactsTrace
  );
  const traceQuery = isContactsTrace ? peopleTrace : landlordTrace;

  const apollo = quota.data;
  // Guard against an older BE (deploy skew: frontends ahead of building-manager-api)
  // that predates `bySource` - Object.entries(undefined) would throw and take the
  // whole card down rather than degrading to "no per-source split".
  const sourceRows = costs.data?.bySource
    ? Object.entries(costs.data.bySource).filter(([, b]) => b.runCount > 0)
    : [];

  return (
    <CollapsibleCard title="Leads costs & API usage" initialOpen={false}>
      <div className="d-flex flex-column gap-3" style={{ fontSize: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Form.Label style={{ margin: 0, fontSize: 12, color: "#888" }}>
            Window
          </Form.Label>
          <Form.Select
            size="sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 160 }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </Form.Select>
          <span style={{ fontSize: 11, color: "#aaa" }}>
            Estimated from token + web-search counts (not billed).
          </span>
        </div>

        {/* Apollo free-tier daily discovery quota ------------------------- */}
        <div style={{ fontSize: 12 }}>
          {quota.isLoading ? (
            <span style={{ color: "#888" }}>Loading Apollo quota...</span>
          ) : !apollo ? (
            <span style={{ color: "#aaa" }}>Apollo quota unavailable.</span>
          ) : !apollo.configured ? (
            <span style={{ color: "#b8860b" }}>
              Apollo People Search is not configured on this environment - the
              &quot;Find people&quot; actions will not run.
            </span>
          ) : apollo.dayLimit != null ? (
            <span>
              <strong>Apollo discovery today:</strong> {apollo.dayConsumed ?? 0}{" "}
              / {apollo.dayLimit} calls used
              {apollo.dayLeft != null && (
                <span style={{ color: "#888" }}> ({apollo.dayLeft} left)</span>
              )}
              <span style={{ color: "#aaa" }}> - free discovery, 0 credits</span>
              <span style={{ color: "#888" }}> - {apolloResetHint()}</span>
            </span>
          ) : (
            <span style={{ color: "#aaa" }}>
              Apollo quota unavailable
              {apollo.error ? ` (${apollo.error})` : ""}.
            </span>
          )}
        </div>

        {costs.isLoading ? (
          <div style={{ color: "#888" }}>Loading costs...</div>
        ) : costs.isError || !costs.data ? (
          <Alert variant="danger">Could not load lead costs.</Alert>
        ) : (
          <>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.totalUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  total ({costs.data.totals.runCount} runs,{" "}
                  {costs.data.totals.testRunCount} test)
                </div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.modelUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>model tokens</div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.webSearchUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  web search ({costs.data.totals.webSearchCount} queries @{" "}
                  {usd(costs.data.webSearchUsdPerQuery)})
                </div>
              </div>
            </div>

            {sourceRows.length > 0 && (
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {sourceRows.map(([label, b]) => (
                  <div key={label} style={{ fontSize: 12 }}>
                    <span style={{ color: "#888" }}>{label}:</span>{" "}
                    <strong>{usd(b.totalUsd)}</strong>{" "}
                    <span style={{ color: "#aaa" }}>
                      ({b.runCount} run{b.runCount === 1 ? "" : "s"})
                    </span>
                  </div>
                ))}
              </div>
            )}

            {costs.data.recent.length === 0 ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                No lead-research runs in this window.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table size="sm" striped hover style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Source</th>
                      <th>Subject</th>
                      <th style={{ textAlign: "right" }}>Searches</th>
                      <th style={{ textAlign: "right" }}>Cost</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.data.recent.map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.createdAt).toLocaleString()}</td>
                        <td>{r.source ?? "-"}</td>
                        <td>
                          {r.subjectRef ?? "-"}
                          {r.isTest && (
                            <Badge bg="info" style={{ marginLeft: 4 }}>
                              test
                            </Badge>
                          )}
                          {r.failed && (
                            <Badge bg="secondary" style={{ marginLeft: 4 }}>
                              failed
                            </Badge>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>{r.webSearchCount}</td>
                        <td style={{ textAlign: "right" }}>{usd(r.totalUsd)}</td>
                        <td>
                          {r.traceRunId && (
                            <Button
                              size="sm"
                              variant="link"
                              style={{ fontSize: 11, padding: 0 }}
                              onClick={() => setTraceOf(r)}
                            >
                              trace
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {costs.data.truncated && (
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    Showing the most recent {costs.data.recent.length} runs;
                    totals above cover the full window.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <Modal
          show={traceOf !== null}
          onHide={() => setTraceOf(null)}
          size="xl"
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 18 }}>
              {isContactsTrace ? "Contacts ranking" : "Research"} run - AI trace
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {traceQuery.isLoading ? (
              <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
            ) : traceQuery.data ? (
              <AITraceViewer trace={traceQuery.data} />
            ) : (
              <div style={{ fontSize: 12, color: "#888" }}>
                No AI trace available for this run.
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </CollapsibleCard>
  );
};

export default LeadResearchCostsCard;
